import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Timestamp } from '@bufbuild/protobuf';
import { SpecraBadge, SpecraButton, SpecraTabBar, SpecraTabPanel } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useMemo, useState } from 'react';
import { useIsViewOnly } from '../../../../contexts/authContext';
import { programRef } from '../../../../lib/access/types';
import { ConfirmDeleteDialog } from '../../_shared/components/ConfirmDeleteDialog';
import { EntityFormDialog } from '../../_shared/components/EntityFormDialog';
import { EntityTable } from '../../_shared/components/EntityTable';
import { EmptyState } from '../../_shared/components/PageStates';
import { PwsChips, parsePwsSections } from '../../_shared/components/PwsChips';
import { CONTRACT_TYPE_CHOICES } from '../../_shared/components/review/reviewItemCopy';
import { deriveOptionalTaskLifecycle, OPTIONAL_TASK_BADGE_LABEL, OPTIONAL_TASK_TAB_LABEL, utcDayNumber } from '../../_shared/lib/optionalTaskLifecycle';
import { presentOptionalTaskTitle } from '../../_shared/lib/optionalTaskTitleCase';
import { formatMoney } from '../../_shared/lib/recordLabels';
import { useContractCLINs } from '../contractHooks';
import { optionalTaskHooks, useOptionalTaskExercisesForProgram } from '../operationalHooks';
import { money, OptionalTaskExercisesDrawer } from './OptionalTaskExercisesDrawer';
// not_exercised / exercised / completed — see the delivery.optional_tasks
// CHECK constraint (000135_optional_task_provenance_and_pop). New rows always
// carry one of these three; a blank status never reaches the client. This
// still backs the manual edit-form Status select (kept for audit/compat), but
// no longer drives tab membership or the rendered badge — see
// optionalTaskLifecycle.ts (SAD-341).
const STATUS_OPTIONS = [
    {
        value: 'not_exercised',
        label: 'Not exercised'
    },
    {
        value: 'exercised',
        label: 'Exercised'
    },
    {
        value: 'completed',
        label: 'Completed'
    }
];
const TAB_ORDER = [
    'all',
    'not_exercised',
    'exercised',
    'completed'
];
// Honest per-tab empty copy — never the generic "no tasks match this filter"
// text, since each tab is a real, named scope now (SAD-341).
const TAB_EMPTY_COPY = {
    all: 'Try a different tab to see other optional tasks on this contract.',
    not_exercised: 'Every optional task on this contract has at least one recorded exercise.',
    exercised: 'No optional tasks currently have a SLIN within its period of performance.',
    completed: 'No optional tasks have a SLIN whose period of performance has ended.'
};
function buildColumns(activeTab, lifecycleByTaskId) {
    // Total is untouched on All/Not-exercised — Matt's SAD-341 work order names
    // Funded only. The Exercised/Completed tabs still recompute Total from only
    // the rows that qualify for that tab.
    //
    // Funded on All/Not-exercised used to fall through to the task's own stored
    // `fundedAmount`, per Matt's earlier "I want to make sure [All] does not
    // change." That is now SUPERSEDED, deliberately, by his 2026-08-31 work
    // order: nothing ever writes `delivery.optional_tasks.funded_amount` when a
    // modification exercises a task (pangaea's markOptionalTaskExercised writes
    // only exercised/status), so the stored figure reads $0 for every
    // mod-exercised task while its real exercise rows carry the true cost. A
    // task with at least one exercise row now sums `cost` across ALL of that
    // task's rows (not just the ones scoped to the active tab) instead of
    // trusting the stored amount; a task with zero exercise rows — the only way
    // to land on Not-exercised — has nothing to sum, so it keeps showing its
    // stored fundedAmount exactly as before.
    const scoped = activeTab === 'exercised' || activeTab === 'completed';
    const scopedRows = (row)=>scoped ? lifecycleByTaskId.get(row.id)?.rowsForTab(activeTab) ?? [] : [];
    const totalFor = (row)=>scoped ? scopedRows(row).reduce((sum, e)=>sum + e.quantity * e.unitPrice, 0) : row.totalAmount;
    // A NULL cost (a word-only exercise names no money, SAD-821) is unknown,
    // not $0: only stated costs sum, and a task none of whose exercises states
    // one shows its stored figure, the one its edit form shows.
    const statedCost = (e)=>e.costIsNull !== true;
    const fundedFor = (row)=>{
        if (scoped) return scopedRows(row).reduce((sum, e)=>sum + e.cost, 0);
        const stated = (lifecycleByTaskId.get(row.id)?.rowsForTab('all') ?? []).filter(statedCost);
        return stated.length > 0 ? stated.reduce((sum, e)=>sum + e.cost, 0) : row.fundedAmount;
    };
    // The task's own type, else the one type its exercises agree on.
    const typeFor = (row)=>{
        if (row.pricingType) return row.pricingType;
        const types = new Set((lifecycleByTaskId.get(row.id)?.rowsForTab('all') ?? []).map((e)=>(e.pricingType ?? '').trim()).filter(Boolean));
        return types.size === 1 ? [
            ...types
        ][0] : '';
    };
    return [
        // Display only — stored `name` is never rewritten (SAD-338).
        {
            key: 'name',
            header: 'Name',
            render: (row)=>presentOptionalTaskTitle(row.name)
        },
        {
            key: 'taskNumber',
            header: 'Task #',
            align: 'right'
        },
        {
            key: 'clinNumber',
            header: 'CLIN',
            align: 'right'
        },
        {
            key: 'pricingType',
            header: 'Type',
            render: (row)=>typeFor(row)
        },
        {
            key: 'pwsSections',
            header: 'PWS',
            render: (row)=>/*#__PURE__*/ _jsx(PwsChips, {
                    sections: row.pwsSections
                })
        },
        {
            // Derived entirely from exercise periods (SAD-341), never from the
            // manually asserted status/exercised fields — see
            // deriveOptionalTaskLifecycle. On a specific tab the badge shows that
            // tab's own label (the row is only present because it qualifies for
            // it); on All it shows the task's overall derived status, so a task
            // exercised under one SLIN and completed under another reads
            // "Exercised" there while still appearing under both real tabs.
            key: 'status',
            header: 'Status',
            render: (row)=>{
                const lifecycle = lifecycleByTaskId.get(row.id);
                const badgeStatus = lifecycle?.badgeStatus ?? 'not_exercised';
                const label = activeTab === 'all' ? OPTIONAL_TASK_BADGE_LABEL[badgeStatus] : OPTIONAL_TASK_TAB_LABEL[activeTab];
                const displayStatus = activeTab === 'all' ? badgeStatus : activeTab;
                const variant = displayStatus === 'not_exercised' ? 'neutral' : displayStatus === 'period_unavailable' ? 'warning' : displayStatus === 'upcoming' ? 'info' : 'success';
                return /*#__PURE__*/ _jsx(SpecraBadge, {
                    variant: variant,
                    "data-testid": Testids.deliveryOperational.optionalTaskStatusBadge,
                    "data-status": displayStatus,
                    children: label
                });
            }
        },
        {
            // The stated "may be exercised up to N times" (K9c-1c), moved only by
            // modifications, so it is never edited here. "—" when no document
            // states one; a stated 0 reads 0.
            key: 'maxExercises',
            header: 'Max exercises',
            headerInfo: 'How many times the contract lets this task be exercised, as the documents state it.',
            align: 'right',
            render: (row)=>/*#__PURE__*/ _jsx("span", {
                    "data-testid": Testids.deliveryOperational.optionalTaskMaxExercises,
                    "data-known": row.maxExercisesKnown ? 'true' : 'false',
                    children: row.maxExercisesKnown ? row.maxExercises.toLocaleString('en-US') : '—'
                })
        },
        {
            key: 'totalAmount',
            header: 'Total',
            align: 'right',
            // Scoped tabs sum quantity*unitPrice from exercise rows, but the
            // "Record exercise" form never captures a per-exercise unit price — so
            // a scoped sum of 0 means "not captured," not "worth nothing." Reuse
            // the drawer's money() so that reads as "—" instead of a fabricated
            // $0 (SAD-341). All/Not-exercised show the stored total, in cents like
            // every Delivery table.
            render: (row)=>scoped ? money(totalFor(row)) : formatMoney(totalFor(row)),
            maskAs: 'total_amount'
        },
        {
            key: 'fundedAmount',
            header: 'Funded',
            align: 'right',
            render: (row)=>formatMoney(fundedFor(row)),
            maskAs: 'funded_amount'
        }
    ];
}
// The type a line carries (CONTRACT_TYPE_CHOICES), plus a stored value
// outside that list so editing never silently drops it.
function typeOptions(stored) {
    return stored && !CONTRACT_TYPE_CHOICES.some((c)=>c.value === stored) ? [
        ...CONTRACT_TYPE_CHOICES,
        {
            value: stored,
            label: stored
        }
    ] : CONTRACT_TYPE_CHOICES;
}
// PWS references are stored as a JSON list and typed as "5.2.1, 5.2.3".
function pwsToInput(stored) {
    return parsePwsSections(stored).join(', ');
}
function pwsFromInput(typed) {
    const refs = [
        ...new Set(typed.split(/[,;\n]/).map((r)=>r.trim()).filter(Boolean))
    ];
    return refs.length > 0 ? JSON.stringify(refs) : '';
}
const fields = (storedType)=>[
        {
            kind: 'text',
            name: 'name',
            label: 'Task name',
            required: true,
            maxLength: 200
        },
        {
            kind: 'number',
            name: 'taskNumber',
            label: 'Task number',
            min: 0,
            step: 1
        },
        {
            kind: 'text',
            name: 'clinNumber',
            label: 'CLIN',
            maxLength: 60,
            placeholder: 'e.g. 10001'
        },
        {
            kind: 'select',
            name: 'pricingType',
            label: 'Type',
            options: typeOptions(storedType)
        },
        {
            kind: 'text',
            name: 'pwsSections',
            label: 'PWS paragraph(s)',
            placeholder: 'e.g. 5.2.1, 5.2.3',
            helpText: 'Separate paragraphs with commas.'
        },
        {
            kind: 'checkbox',
            name: 'exercised',
            label: 'Exercised'
        },
        {
            kind: 'select',
            name: 'status',
            label: 'Status',
            required: true,
            options: STATUS_OPTIONS
        },
        {
            kind: 'textarea',
            name: 'description',
            label: 'Description',
            rows: 3
        },
        {
            kind: 'textarea',
            name: 'requirementText',
            label: 'Requirement text',
            rows: 4,
            helpText: 'The complete extracted requirement/source text this task was derived from — manual entry/correction fallback for when extraction is missing or wrong.'
        },
        {
            kind: 'currency',
            name: 'fundedAmount',
            label: 'Funded amount ($)',
            min: 0
        },
        {
            kind: 'currency',
            name: 'totalAmount',
            label: 'Total amount ($)',
            min: 0
        }
    ];
// Optional tasks (option exercises) are keyed by a "CLIN-suffix" number like
// "6001-9". The server lists them by task_number, which leaves the CLIN column
// visibly out of order. Sort by parent CLIN then numeric exercise suffix so
// 6001-2 precedes 6001-10 and 6001-9 precedes 6001-14 — numeric collation does
// both in one comparison. id is the deterministic tiebreaker; CLIN-less tasks
// sort last.
const clinCollator = new Intl.Collator(undefined, {
    numeric: true,
    sensitivity: 'base'
});
function byClinNumber(a, b) {
    if (!a.clinNumber !== !b.clinNumber) return a.clinNumber ? -1 : 1;
    const byClin = clinCollator.compare(a.clinNumber, b.clinNumber);
    if (byClin !== 0) return byClin;
    // Plain code-unit compare on the unique id — an airtight, engine-independent
    // tiebreaker (the numeric collator could collate two distinct ids to 0).
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}
function withWindow(e, w) {
    return Object.assign(Object.create(Object.getPrototypeOf(e)), e, w);
}
// The window an exercise is read with. Its own; else the span its
// contributing SLINs state (first start to last end); else, for an exercise
// recorded from wording alone (no SLIN, no stated money), the span of its
// task's own CLIN lines (SAD-821). Read-time only: nothing is written. With
// none of these it stays "Period unavailable".
function exerciseWindow(e, taskLines) {
    if (e.popStartDate && e.popEndDate) return e;
    const slins = (e.contributingSlins ?? []).filter((s)=>s.popStartDate && s.popEndDate);
    if (slins.length === 0) {
        const wordOnly = (e.contributingSlins ?? []).length === 0 && e.costIsNull !== false && !e.cost;
        return wordOnly && taskLines ? withWindow(e, taskLines) : e;
    }
    const day = (t)=>t.toDate().getTime();
    const popStartDate = slins.reduce((a, s)=>s.popStartDate && a.popStartDate && day(s.popStartDate) < day(a.popStartDate) ? s : a).popStartDate;
    const popEndDate = slins.reduce((a, s)=>s.popEndDate && a.popEndDate && day(s.popEndDate) > day(a.popEndDate) ? s : a).popEndDate;
    return withWindow(e, {
        popStartDate,
        popEndDate
    });
}
// A task's own CLIN lines: those carrying its task number, or its CLIN family.
// Their window runs from the earliest start to the latest end they state.
function taskLineWindow(task, lines) {
    const family = (task.clinNumber ?? '').trim();
    let start = '';
    let end = '';
    for (const l of lines){
        const own = task.taskNumber > 0 && l.optionalTaskNumber === task.taskNumber || !!family && (l.clinNumber === family || l.parentClinNumber === family);
        if (!own) continue;
        const s = (l.popStartDate ?? '').trim();
        const f = (l.popEndDate ?? '').trim();
        if (s && (!start || s < start)) start = s;
        if (f && f > end) end = f;
    }
    if (!start || !end) return undefined;
    const ts = (d)=>Timestamp.fromDate(new Date(`${d}T00:00:00Z`));
    return {
        popStartDate: ts(start),
        popEndDate: ts(end)
    };
}
export function OptionalTasksTab({ programId, referenceDate }) {
    const isViewOnly = useIsViewOnly();
    const { data, isLoading } = optionalTaskHooks.useList({
        programId
    });
    const { data: allExercises } = useOptionalTaskExercisesForProgram(programId);
    const items = useMemo(()=>data?.items ?? [], [
        data?.items
    ]);
    const sortedTasks = useMemo(()=>[
            ...items
        ].sort(byClinNumber), [
        items
    ]);
    const upsert = optionalTaskHooks.useUpsert();
    const remove = optionalTaskHooks.useDelete();
    // Single source of "now" for this render — shared by the lifecycle
    // derivation below, the Status badge (which reads off lifecycleByTaskId),
    // and the drawer, so all three can never classify a task differently.
    const effectiveReferenceDate = referenceDate ?? new Date();
    const [formOpen, setFormOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [deleting, setDeleting] = useState(null);
    const [ledgerTask, setLedgerTask] = useState(null);
    const [activeTab, setActiveTab] = useState('all');
    const { data: clinData } = useContractCLINs(programId);
    const exercisesByTaskId = useMemo(()=>{
        const lines = clinData?.clins ?? [];
        const lineWindows = new Map(items.map((t)=>[
                t.id,
                taskLineWindow(t, lines)
            ]));
        const map = new Map();
        for (const raw of allExercises ?? []){
            const e = exerciseWindow(raw, lineWindows.get(raw.optionalTaskId));
            const bucket = map.get(e.optionalTaskId);
            if (bucket) bucket.push(e);
            else map.set(e.optionalTaskId, [
                e
            ]);
        }
        return map;
    }, [
        allExercises,
        clinData?.clins,
        items
    ]);
    // Lifecycle classification is calendar-day granular (see
    // deriveOptionalTaskLifecycle/utcDayNumber), so this memo is keyed on the
    // UTC day number rather than on effectiveReferenceDate itself or on the raw
    // referenceDate prop. Two bugs that would otherwise trade off against each
    // other: keying on the prop (stable at `undefined` in production) let this
    // memo cache a `new Date()` from whenever it last ran and never refresh it,
    // going stale relative to the drawer's always-fresh `effectiveReferenceDate`;
    // keying on effectiveReferenceDate's object identity would instead recompute
    // on every render (it's a brand-new Date when referenceDate is unset),
    // defeating the memo. The UTC day number changes exactly when the
    // classification actually could, and is otherwise referentially stable.
    const referenceDayKey = utcDayNumber(effectiveReferenceDate);
    // biome-ignore lint/correctness/useExhaustiveDependencies: effectiveReferenceDate is intentionally excluded — see referenceDayKey above.
    const lifecycleByTaskId = useMemo(()=>{
        const map = new Map();
        for (const t of items){
            map.set(t.id, deriveOptionalTaskLifecycle(exercisesByTaskId.get(t.id) ?? [], effectiveReferenceDate));
        }
        return map;
    }, [
        items,
        exercisesByTaskId,
        referenceDayKey
    ]);
    const columns = useMemo(()=>buildColumns(activeTab, lifecycleByTaskId), [
        activeTab,
        lifecycleByTaskId
    ]);
    const counts = useMemo(()=>{
        const c = {
            all: items.length,
            not_exercised: 0,
            exercised: 0,
            completed: 0
        };
        for (const t of items){
            const tabs = lifecycleByTaskId.get(t.id)?.tabs ?? new Set();
            if (tabs.has('not_exercised')) c.not_exercised++;
            if (tabs.has('exercised')) c.exercised++;
            if (tabs.has('completed')) c.completed++;
        }
        return c;
    }, [
        items,
        lifecycleByTaskId
    ]);
    const filteredTasks = useMemo(()=>activeTab === 'all' ? sortedTasks : sortedTasks.filter((t)=>lifecycleByTaskId.get(t.id)?.tabs.has(activeTab)), [
        sortedTasks,
        activeTab,
        lifecycleByTaskId
    ]);
    const handleSubmit = async (values)=>{
        await upsert.mutateAsync({
            id: editing?.id ?? '',
            programId,
            name: values.name,
            taskNumber: Number(values.taskNumber) || 0,
            exercised: Boolean(values.exercised),
            description: values.description ?? '',
            fundedAmount: Number(values.fundedAmount) || 0,
            totalAmount: Number(values.totalAmount) || 0,
            requirementText: values.requirementText ?? '',
            status: values.status || 'not_exercised',
            clinNumber: (values.clinNumber ?? '').trim(),
            pricingType: values.pricingType ?? '',
            pwsSections: pwsFromInput(values.pwsSections ?? '')
        });
        setFormOpen(false);
    };
    const handleDelete = async ()=>{
        if (!deleting) return;
        await remove.mutateAsync(deleting.id);
        setDeleting(null);
    };
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-4",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-end justify-between gap-3",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        children: [
                            /*#__PURE__*/ _jsx("h1", {
                                className: "font-display text-[length:var(--fs-ui-page-title)] font-medium tracking-[-0.01em]",
                                children: "Optional Tasks"
                            }),
                            /*#__PURE__*/ _jsx("p", {
                                className: "text-[12.5px] text-muted-foreground",
                                children: items.length === 0 ? 'No optional tasks on this contract yet.' : `${items.length} optional task${items.length === 1 ? '' : 's'}`
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        onClick: ()=>{
                            setEditing(null);
                            setFormOpen(true);
                        },
                        disabled: isViewOnly,
                        "data-testid": Testids.deliveryOperational.optionalTaskCreate,
                        children: "Add Optional Task"
                    })
                ]
            }),
            items.length > 0 && /*#__PURE__*/ _jsx(SpecraTabBar, {
                label: "Optional task status",
                idBase: "optional-tasks",
                value: activeTab,
                onValueChange: setActiveTab,
                "data-testid": Testids.deliveryOperational.optionalTaskTabList,
                options: TAB_ORDER.map((tab)=>({
                        value: tab,
                        label: OPTIONAL_TASK_TAB_LABEL[tab],
                        count: tab === 'all' ? counts.all : counts[tab],
                        testId: Testids.deliveryOperational.optionalTaskTab
                    }))
            }),
            /*#__PURE__*/ _jsx(SpecraTabPanel, {
                idBase: "optional-tasks",
                value: activeTab,
                active: true,
                "data-testid": Testids.deliveryOperational.optionalTaskTabPanel,
                children: /*#__PURE__*/ _jsx(EntityTable, {
                    data: filteredTasks,
                    columns: columns,
                    testid: Testids.deliveryOperational.optionalTasksTable,
                    rowTestid: Testids.deliveryOperational.optionalTaskRow,
                    rowKey: (t)=>t.id,
                    isLoading: isLoading,
                    hideRowActions: isViewOnly,
                    resource: programRef(programId),
                    emptyState: items.length === 0 ? /*#__PURE__*/ _jsx(EmptyState, {
                        title: "No optional tasks on this contract",
                        description: "Optional tasks are option line items the government can exercise by modification. They appear here after the award is extracted, or you can add one manually.",
                        action: /*#__PURE__*/ _jsx(SpecraButton, {
                            onClick: ()=>{
                                setEditing(null);
                                setFormOpen(true);
                            },
                            disabled: isViewOnly,
                            children: "Add Optional Task"
                        })
                    }) : /*#__PURE__*/ _jsx(EmptyState, {
                        title: `No optional tasks are ${OPTIONAL_TASK_TAB_LABEL[activeTab].toLowerCase()}`,
                        description: TAB_EMPTY_COPY[activeTab],
                        action: /*#__PURE__*/ _jsx(SpecraButton, {
                            variant: "ghost",
                            onClick: ()=>setActiveTab('all'),
                            children: "Show all optional tasks"
                        })
                    }),
                    onRowClick: (row)=>setLedgerTask(row),
                    rowActions: (row)=>/*#__PURE__*/ _jsxs("div", {
                            className: "flex gap-2 justify-end",
                            children: [
                                /*#__PURE__*/ _jsx(SpecraButton, {
                                    size: "sm",
                                    variant: "ghost",
                                    onClick: ()=>setLedgerTask(row),
                                    "data-testid": Testids.optionalTasks.openExercises,
                                    children: "Exercises"
                                }),
                                /*#__PURE__*/ _jsx(SpecraButton, {
                                    size: "sm",
                                    variant: "ghost",
                                    onClick: ()=>{
                                        setEditing(row);
                                        setFormOpen(true);
                                    },
                                    "data-testid": Testids.deliveryOperational.optionalTaskEdit,
                                    children: "Edit"
                                }),
                                /*#__PURE__*/ _jsx(SpecraButton, {
                                    size: "sm",
                                    variant: "ghost",
                                    className: "text-danger hover:bg-danger-muted hover:text-danger",
                                    onClick: ()=>setDeleting(row),
                                    "data-testid": Testids.deliveryOperational.optionalTaskDelete,
                                    children: "Delete"
                                })
                            ]
                        })
                })
            }),
            /*#__PURE__*/ _jsx(EntityFormDialog, {
                open: formOpen,
                onOpenChange: setFormOpen,
                title: editing ? 'Edit Optional Task' : 'Add Optional Task',
                fields: fields(editing?.pricingType ?? ''),
                defaultValues: {
                    id: editing?.id ?? '',
                    name: editing?.name ?? '',
                    taskNumber: editing?.taskNumber ?? 0,
                    exercised: editing?.exercised ?? false,
                    description: editing?.description ?? '',
                    fundedAmount: editing?.fundedAmount ?? 0,
                    totalAmount: editing?.totalAmount ?? 0,
                    requirementText: editing?.requirementText ?? '',
                    status: editing?.status || 'not_exercised',
                    clinNumber: editing?.clinNumber ?? '',
                    pricingType: editing?.pricingType ?? '',
                    pwsSections: pwsToInput(editing?.pwsSections ?? '')
                },
                onSubmit: handleSubmit,
                isSubmitting: upsert.isPending
            }),
            ledgerTask && /*#__PURE__*/ _jsx(OptionalTaskExercisesDrawer, {
                programId: programId,
                task: ledgerTask,
                scopeTab: activeTab,
                referenceDate: effectiveReferenceDate,
                onClose: ()=>setLedgerTask(null)
            }),
            /*#__PURE__*/ _jsx(ConfirmDeleteDialog, {
                open: !!deleting,
                onOpenChange: (open)=>!open && setDeleting(null),
                title: "Delete Optional Task",
                description: deleting ? `Delete "${presentOptionalTaskTitle(deleting.name)}"? This cannot be undone.` : undefined,
                onConfirm: handleDelete,
                isPending: remove.isPending
            })
        ]
    });
}
